import {md2html} from '@github-ui/md2html'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {
  AddCommentMutationMutation,
  CommentAuthorAssociation,
} from '@github-ui/server-persisted-queries/types/addCommentMutation'

export type AddCommentOptimisticInput = {
  issue: RelayCompat<AddCommentIssueComment['issue']>
  repository: RelayCompat<AddCommentIssueComment['repository']>
  authorAssociation: CommentAuthorAssociation
  commentAuthor: {
    id: string
    login: string
    name?: string | null
    avatarUrl: string
  }
}

type AddCommentIssueComment = Extract<
  NonNullable<NonNullable<NonNullable<AddCommentMutationMutation['addComment']>['timelineEdge']>['node']>,
  {__typename: 'IssueComment'}
>

export function getAddCommentOptimisticNode(
  optimisticInput: AddCommentOptimisticInput | undefined,
  withOptimisticResponse: boolean,
  body: string,
): (Record<string, unknown> & {id: string}) | undefined {
  if (!optimisticInput || !withOptimisticResponse) {
    return undefined
  }
  // The paved-path viewer supplies query data without populating Relay's store.
  const {issue, repository} = optimisticInput
  const {author} = issue
  const {owner} = repository

  return {
    __typename: 'IssueComment',
    __isNode: 'IssueComment',
    id: `temp-comment-${crypto.randomUUID()}`,
    body: body ? body : '',
    bodyHTML: md2html(body),
    bodyVersion: '1', // Initial version, will be updated on server
    createdAt: new Date().toISOString(),
    lastEditedAt: null,
    viewerCanReadUserContentEdits: true,
    lastUserContentEdit: null,
    // Add other required fields with default/null values
    author: {
      __typename: 'User',
      id: optimisticInput.commentAuthor.id,
      avatarUrl: optimisticInput.commentAuthor.avatarUrl,
      login: optimisticInput.commentAuthor.login,
      name: optimisticInput.commentAuthor.name,
    },
    authorAssociation: optimisticInput.authorAssociation,
    authorToRepoOwnerSponsorship: null,
    createdViaEmail: false,
    viaApp: null,
    databaseId: null,
    isHidden: false,
    issue: {
      id: issue.id,
      databaseId: issue.databaseId,
      locked: issue.locked,
      number: issue.number,
      author: author ? {__typename: author.__typename, id: author.id, login: author.login} : null,
    },
    minimizedReason: null,
    pendingMinimizeReason: null,
    pendingBlock: null,
    pendingUnblock: null,
    reactionGroups: [],
    repository: {
      id: repository.id,
      databaseId: repository.databaseId,
      isPrivate: repository.isPrivate,
      name: repository.name,
      nameWithOwner: repository.nameWithOwner,
      slashCommandsEnabled: repository.slashCommandsEnabled,
      viewerCanCreateIssues: repository.viewerCanCreateIssues,
      owner: {
        __typename: owner.__typename,
        id: owner.id,
        login: owner.login,
        url: owner.url,
      },
    },
    showSpammyBadge: false,
    url: '',
    viewerCanBlockFromOrg: false,
    viewerCanBlockPersonally: false,
    viewerCanDelete: false,
    viewerCanMinimize: false,
    viewerCanUnminimize: false,
    viewerCanReport: false,
    viewerCanReportToMaintainer: false,
    viewerCanUnblockFromOrg: false,
    viewerCanUnblockPersonally: false,
    viewerCanUpdate: false,
    viewerDidAuthor: true,
    viewerCanPin: false,
    viewerCanUnpin: false,
    pinnedBy: null,
  }
}
